<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'

import {
  getPlayerStatConfigs,
  getTeamStatConfigs,
  SportsStatConfig,
  SportsStatIdConfig,
  Statistics,
  UnsavedChangesConfirmModal,
} from '@collector/desktop-feature-statistics'
import { StatusChip, useConfirmModal } from '@collector/shared-ui'
import BackToRelationButton from '@desktop/views/Relation/components/RelationView/BackToRelationButton.vue'
import Result from '@desktop/views/Relation/components/RelationView/Summary/Result.vue'
import Summary from '@desktop/views/Relation/components/RelationView/Summary/Summary.vue'
import {
  useEvent,
  useParticipants,
  useSport,
  useSubParticipants,
  useSwitchTeamColors,
} from '@desktop/views/Relation/composables'
import SummaryParticipantsImportantStats from '@desktop/views/Relation/Sports/components/RelationView/SummaryParticipantsImportantStats.vue'
import { useElementBounding } from '@vueuse/core'

type Props = {
  playerStatIdConfigs: SportsStatIdConfig[]
  teamStatIdConfigs: SportsStatIdConfig[]
  teamStatsAccordionIdConfigs: SportsStatIdConfig[]
  excludeAbsentPlayerStats?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  excludeAbsentPlayerStats: true,
})

// composables
const { isSwitchedTeamColors } = useSwitchTeamColors()
const { openModalForConfirm } = useConfirmModal()
const { isResultVerified, isStatsVerified, event } = useEvent()
const { sport } = useSport()
const {
  homeParticipant,
  awayParticipant,
  setHomeParticipant,
  setAwayParticipant,
} = useParticipants()
const {
  awaySubParticipants,
  homeSubParticipants,
  setHomeSubParticipants,
  setAwaySubParticipants,
} = useSubParticipants()
const stickyHeader = useTemplateRef('stickyHeader')
const { top: stickyHeaderTop } = useElementBounding(stickyHeader)

// state
const dirty = ref(false)

// computed
const statusText = computed(() => {
  if (isResultVerified.value && isStatsVerified.value) {
    return 'The result and stats have been verified'
  }
  if (isResultVerified.value) {
    return 'The result has been verified'
  }
  if (isStatsVerified.value) {
    return 'The stats have been verified'
  }
  return ''
})

const teamStats = computed<SportsStatConfig[]>(() => {
  const home = homeParticipant.value
  const away = awayParticipant.value
  if (!home || !away) return []
  return getTeamStatConfigs(
    props.teamStatIdConfigs,
    sport.value.stats.team,
    home,
    away,
  )
})
const teamStatsAccordion = computed<SportsStatConfig[]>(() => {
  const home = homeParticipant.value
  const away = awayParticipant.value
  if (!home || !away) return []
  return getTeamStatConfigs(
    props.teamStatsAccordionIdConfigs,
    sport.value.stats.team,
    home,
    away,
  )
})
const playerStats = computed<SportsStatConfig[]>(() =>
  getPlayerStatConfigs(
    props.playerStatIdConfigs,
    sport.value.stats.person,
    homeSubParticipants.value,
    awaySubParticipants.value,
    props.excludeAbsentPlayerStats,
  ),
)

// guards
onBeforeRouteLeave(async () => {
  if (dirty.value) return await openModalForConfirm(UnsavedChangesConfirmModal)
})
</script>

<template>
  <div class="relative grid h-full grid-rows-[auto_1fr] gap-2">
    <div
      ref="stickyHeader"
      class="sticky top-0 z-10 -mx-2 -my-3 px-2 py-3 transition"
      :class="{
        'bg-neutral-light-10/70 shadow-md backdrop-blur-md':
          stickyHeaderTop === 0,
      }"
    >
      <BackToRelationButton />
    </div>
    <Statistics
      v-if="homeParticipant && awayParticipant"
      :homeParticipant
      :awayParticipant
      :homePlayers="homeSubParticipants"
      :awayPlayers="awaySubParticipants"
      :eventId="event.id"
      :teamStats
      :teamStatsAccordion
      :playerStats
      :isStatsVerified
      :isVerifyStatsButtonDisabled="!isResultVerified || isStatsVerified"
      :teamColorsSwitched="isSwitchedTeamColors"
      @update:homeParticipant="setHomeParticipant"
      @update:awayParticipant="setAwayParticipant"
      @update:awayPlayers="setAwaySubParticipants"
      @update:homePlayers="setHomeSubParticipants"
      @update:dirty="dirty = $event"
    >
      <template #summary>
        <Summary>
          <template #result>
            <Result />
          </template>
          <template #bottom>
            <SummaryParticipantsImportantStats>
              <StatusChip
                v-if="isResultVerified"
                variant="success"
              >
                {{ statusText }}
              </StatusChip>
            </SummaryParticipantsImportantStats>
          </template>
        </Summary>
      </template>
    </Statistics>
  </div>
</template>
